import { Box, Grid } from '@radix-ui/themes';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTextInput } from 'components/common/form/text';
import { MachineContext } from 'contexts/machine.context';
import { DECIMALS_SPEED, DECIMALS_SPIN } from 'enums/pitches';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { FC, useContext, useEffect, useState } from 'react';
import { usePitchDesignStore } from 'stores/use-pitch-design-store';

export const PDSpinsModeForm: FC = () => {
  const showCurrent = usePitchDesignStore((s) => s.showCurrent);
  const inputs = usePitchDesignStore((s) => s.inputs);
  const refBall = usePitchDesignStore((s) => s.refBall);
  const { speedMph, spinExt } = inputs;

  const { machine, unitFactors } = useContext(MachineContext);

  const [speedText, setSpeedText] = useState(
    (unitFactors.fromSpeedMph * speedMph).toFixed(DECIMALS_SPEED)
  );

  const latestSpeedMph = useLatest(speedMph);

  // e.g. if we reload machine in the background from admin changes
  useEffect(() => {
    const value = unitFactors.fromSpeedMph * latestSpeedMph.current;
    setSpeedText(value.toFixed(DECIMALS_SPEED));
  }, [unitFactors.fromSpeedMph]);

  const [wnetText, setWnetText] = useState(spinExt.wnet.toFixed(DECIMALS_SPIN));
  const [gyroText, setGyroText] = useState(
    spinExt.gyro_angle.toFixed(DECIMALS_SPIN)
  );
  const [waxisText, setWaxisText] = useState(
    spinExt.waxis.toFixed(DECIMALS_SPIN)
  );

  return (
    <ErrorBoundary identifier="PDSpinsModeForm">
      <Grid columns="4" gap={RADIX.FLEX.GAP.SM}>
        <Box>
          <CommonTextInput
            id="pitch-design-speed"
            label={t('pd.speed-units', {
              units: machine.isMetric ? 'kph' : 'mph',
            }).toString()}
            inputColor={
              PitchDesignHelper.validateBallSpeedMph(speedMph)
                ? undefined
                : RADIX.COLOR.WARNING
            }
            type="number"
            value={speedText}
            onChange={(v) => setSpeedText(v ?? '')}
            onNumericChange={(v) =>
              usePitchDesignStore
                .getState()
                .mergeInputs({ speedMph: v / unitFactors.fromSpeedMph })
            }
            hint_md={
              showCurrent
                ? t('pd.current-x', {
                    x: (unitFactors.fromSpeedMph * refBall.speed).toFixed(
                      DECIMALS_SPEED
                    ),
                  }).toString()
                : undefined
            }
          />
        </Box>

        <Box>
          <CommonTextInput
            id="pitch-design-spin-net"
            label="pd.net-spin"
            inputColor={
              PitchDesignHelper.validateNetSpin(spinExt.wnet)
                ? undefined
                : RADIX.COLOR.WARNING
            }
            type="number"
            value={wnetText}
            onChange={(v) => setWnetText(v ?? '')}
            // NET SPIN - NET MODE
            onNumericChange={(v) =>
              usePitchDesignStore
                .getState()
                .mergeInputs({ spinExt: { ...spinExt, wnet: v } })
            }
            hint_md={
              showCurrent
                ? t('pd.current-x', {
                    x: refBall.wnet.toFixed(DECIMALS_SPIN),
                  }).toString()
                : undefined
            }
          />
        </Box>
        <Box>
          <CommonTextInput
            id="pitch-design-gyro"
            label="pd.gyro-angle-deg"
            inputColor={
              PitchDesignHelper.validateGyroAngle(spinExt.gyro_angle)
                ? undefined
                : RADIX.COLOR.WARNING
            }
            type="number"
            value={gyroText}
            onChange={(v) => {
              setGyroText(v ?? '');
            }}
            onNumericChange={(v) =>
              usePitchDesignStore
                .getState()
                .mergeInputs({ spinExt: { ...spinExt, gyro_angle: v } })
            }
            hint_md={
              showCurrent
                ? t('pd.current-x', {
                    x: refBall.gyro_angle.toFixed(DECIMALS_SPIN),
                  }).toString()
                : undefined
            }
          />
        </Box>
        <Box>
          <CommonTextInput
            id="pitch-design-spin-axis"
            label="pd.spin-axis-deg"
            inputColor={
              PitchDesignHelper.validateSpinAxis(spinExt.waxis)
                ? undefined
                : RADIX.COLOR.WARNING
            }
            type="number"
            value={waxisText}
            onChange={(v) => {
              setWaxisText(v ?? '');
            }}
            onNumericChange={(v) =>
              usePitchDesignStore
                .getState()
                .mergeInputs({ spinExt: { ...spinExt, waxis: v } })
            }
            hint_md={
              showCurrent
                ? t('pd.current-x', {
                    x: refBall.waxis.toFixed(DECIMALS_SPIN),
                  }).toString()
                : undefined
            }
          />
        </Box>
      </Grid>
    </ErrorBoundary>
  );
};
